import { Card, CardBody } from '@vnetwork/ui/primitives';
import { Timeline, type TimelineItem } from '@vnetwork/ui/data';
import { useEntityLogs, type WarehouseActivityLog } from '@/api/capacities';
import { formatDateTime } from '@/lib/format';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';

/** One activity-log row → a Timeline item (loosely-shaped log fields). */
function toTimelineItem(log: WarehouseActivityLog, index: number): TimelineItem {
  const title = log.action || log.description || 'Activity';
  const description = log.action && log.description ? log.description : undefined;
  return {
    id: index,
    title,
    description,
    meta: log.employee_name || undefined,
    timestamp: formatDateTime(log.created_at),
    tone: 'neutral',
  };
}

/**
 * Activity tab for a rack — `useEntityLogs('racks', id)` rendered as a
 * `Timeline`. Loading → skeleton, error → retry, empty → Timeline's empty text.
 */
export function RackActivityTab({ rackId }: { rackId: string }) {
  const { data, isLoading, isError, refetch } = useEntityLogs('racks', rackId);

  if (isError) {
    return <TabError title="Failed to load activity" onRetry={() => refetch()} />;
  }
  if (isLoading) {
    return <TabLoading rows={5} />;
  }

  const items = (data?.results ?? []).map(toTimelineItem);

  return (
    <Card flat>
      <CardBody>
        <Timeline items={items} emptyText="No activity recorded for this rack." />
      </CardBody>
    </Card>
  );
}
